import { Component, OnInit, OnDestroy, AfterViewInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { NgxFlickeringGridComponent } from '@omnedia/ngx-flickering-grid';
import { NgxTypewriterComponent } from '@omnedia/ngx-typewriter';
import { NgxHighlighterComponent } from "@omnedia/ngx-highlighter";
import { NgxThreeGlobeComponent } from '@omnedia/ngx-three-globe';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { EmailServiceService } from '../../service/email-service.service';
import { FooterComponent } from '../../components/footer.component';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule,
    NgxFlickeringGridComponent,
    NgxTypewriterComponent,
    NgxHighlighterComponent,
    NgxThreeGlobeComponent,
    FormsModule,
    FooterComponent
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class HomeComponent implements OnInit, OnDestroy, AfterViewInit {
  private observer?: IntersectionObserver;
  private carouselInterval?: number;
  private subscriptions: Subscription[] = [];
  private isViewInitialized = false;
  private pendingFragment: string | null = null;

  contactFormData: any = {
    name: '',
    email: '',
    subject: '',
    message: ''
  };
  submissionStatus: 'success' | 'error' | null = null;
  isSubmitting = false;
  private timeline: gsap.core.Timeline | null = null;

  constructor(
    private emailService: EmailServiceService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit() {
    // Listen for incoming query params and fragments to prefill/scroll
    const qpSub = this.route.queryParams.subscribe((params) => {
      if (params && params['subject']) {
        this.contactFormData.subject = params['subject'];
      }
    });

    const fragSub = this.route.fragment.subscribe((frag) => {
      if (frag) {
        if (this.isViewInitialized) {
          setTimeout(() => this.scrollToFragment(frag), 80);
        } else {
          this.pendingFragment = frag;
        }
      }
    });

    const navSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const params = this.route.snapshot.queryParams;
        if (params && params['subject']) {
          this.contactFormData.subject = params['subject'];
        }
        const frag = this.route.snapshot.fragment;
        if (frag) {
          if (this.isViewInitialized) {
            setTimeout(() => this.scrollToFragment(frag), 80);
          } else {
            this.pendingFragment = frag;
          }
        }
      }
    });

    this.subscriptions.push(qpSub, fragSub, navSub);
  }

  ngAfterViewInit() {
    this.initScrollAnimations();
    this.initOurClientsAnimation();
    this.isViewInitialized = true;
    console.log('Logging data');
    console.log(this.contactFormData.subject);
    console.log(this.pendingFragment);
    if (this.pendingFragment) {
      const frag = this.pendingFragment;
      // allow layout & third-party plugins to initialize
      setTimeout(() => this.scrollToFragment(frag as string), 150);
      this.pendingFragment = null;
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.carouselInterval) {
      clearInterval(this.carouselInterval);
    }
    if (this.timeline) {
      this.timeline.kill();
    }
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());
  }

  private scrollToFragment(fragment: string) {
    console.log(`Scrolling to fragment: ${fragment}`);
    // Try id first, then class, then name
    const el = document.getElementById(fragment) || document.querySelector(`.${fragment}`) as HTMLElement || document.querySelector(`[name="${fragment}"]`) as HTMLElement;
    if (!el) return;

    // Give GSAP/ScrollTrigger a chance to refresh and allow layout to settle
    try {
      if ((ScrollTrigger as any) && (ScrollTrigger as any).refresh) {
        (ScrollTrigger as any).refresh();
      }
    } catch (e) {
      // ignore
    }

    const navHeight = 118; // adjust if necessary
    const targetTop = el.getBoundingClientRect().top + window.scrollY - navHeight;

    // Use a brief timeout and multiple rAFs to ensure other scroll handlers don't overwrite this
    setTimeout(() => {
      window.scrollTo({ top: targetTop, behavior: 'smooth' });

      // ensure final position after any framework scroll restoration
      requestAnimationFrame(() => requestAnimationFrame(() => {
        window.scrollTo({ top: targetTop, behavior: 'auto' });
      }));

      // focus subject input if present
      const subjectInput = document.getElementById('subject') as HTMLInputElement | null;
      if (subjectInput) {
        subjectInput.focus();
      }
    }, 80);
  }

  private initScrollAnimations() {
    // Create intersection observer for scroll-triggered animations
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLElement;
            const delay = element.getAttribute('data-delay');

            if (delay) {
              setTimeout(() => {
                element.classList.add('animate-in');
              }, parseInt(delay));
            } else {
              element.classList.add('animate-in');
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe all elements with animation data attributes
    const animatedElements = document.querySelectorAll('[data-animate]');
    animatedElements.forEach((element) => {
      if (this.observer) {
        this.observer.observe(element);
      }
    });
  }

  private initOurClientsAnimation() {
    gsap.registerPlugin(ScrollTrigger);
    const contentElements = document.querySelectorAll('.offers-block');
    const main = document.querySelector('.slide');

    contentElements.forEach((el) => {
      console.log(el);
      const initialRotate = Math.random() * 2 - 2;
      const initialY = Math.random() * 2 - 2;
      const initialX = Math.random() * 2 - 2;

      gsap.set(el, {
        rotate: initialRotate,
        yPercent: initialY,
        xPercent: initialX
      });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: el,
            scroller: window.innerWidth < 1100 ? main : null,
            start: 'center 60%',
            end: '+=60%',
            scrub: 1
          },
        })
        .to(el, {
          ease: 'none',
          rotate:
            initialRotate +
            (Math.random() > 0.5 ? Math.random() * 7 : -Math.random() * 7),
          yPercent:
            initialY +
            (Math.random() > 0.5 ? Math.random() * 7 : -Math.random() * 7),
          scale: 0.9,
        });
    });
  }

  submitContactForm(form: NgForm) {
    if (form.invalid) {
      return;
    }

    this.isSubmitting = true;
    this.submissionStatus = null;

    this.emailService.sendMail(this.contactFormData).subscribe({
      next: (data) => {

        if (data.success) {
          this.submissionStatus = 'success';
          form.resetForm();
        }
        this.isSubmitting = false;
      },
      error: (err) => {
        this.submissionStatus = 'error';
        this.isSubmitting = false;
      },
    });
  }
}
